import React, { Fragment, useMemo, useEffect } from "react";
import { returnToUriClickHandler } from "../../../../utils/returnToUrlClickHandler";
import PropTypes from "prop-types";
import {
  Spinner,
  Toast,
  useMediaQuery
} from "@ebscois/react-component-library";

import { useDispatch, useStore } from "../../../../store";
import {
  handleAppSwitch,
  resetAppSwitcherData
} from "../../../../store/actions";
import {
  AFFORDANCE,
  NAVIGATE_TO,
  logNavigationClicked
} from "../../../../utils/analytics";
import useHeaderOffset from "./hooks/useHeaderOffset";
import {
  MaxHeightImg,
  ContainerWithBackground,
  ContainerWithSidelights,
  AppSwitchOption,
  Sidelight
} from "./AppSwitcher.styles";

const AppSwitcher = ({ appSwitcherData }) => {
  const store = useStore();
  const { isFetching, dataId, hasError, errorMsg, appSwitcherUrl } =
    store.appSwitcher;
  const dispatch = useDispatch();
  const shouldWidgetRender = useMemo(
    () => appSwitcherData.length > 1,
    [appSwitcherData]
  );
  useHeaderOffset(shouldWidgetRender);

  useEffect(() => {
    if (appSwitcherUrl?.route) {
      if (appSwitcherUrl.openInNewTab) {
        window.open(appSwitcherUrl.route, "_blank");
      } else {
        window.location.assign(appSwitcherUrl.route);
      }
      resetAppSwitcherData(dispatch);
    }
  }, [store, appSwitcherUrl]);

  const isMobile = useMediaQuery("max", "sm");

  const handleButtonClick = (event, data) => {
    const { id, seamlessAuthUrl } = data;
    logNavigationClicked({
      affordance: AFFORDANCE.productTab,
      navigateTo: NAVIGATE_TO[id],
      navigateFrom: null
    });
    if (seamlessAuthUrl) {
      returnToUriClickHandler(event, seamlessAuthUrl);
    } else {
      handleAppSwitch(dispatch, id);
    }
  };
  if (!shouldWidgetRender) {
    return <Fragment />;
  }
  return (
    <ContainerWithBackground>
      <ContainerWithSidelights>
        <Sidelight $isLeftSidelight={true} />
        {appSwitcherData.map((data, index) => (
          <AppSwitchOption
            $shouldHaveGradientBg={index === 0}
            key={data.id}
            data-testid={data.id}
            aria-current={index === 0 ? "page" : false}
            onClick={(event) => {
              event.preventDefault();
              if (index !== 0) {
                handleButtonClick(event, data);
              } else {
                return;
              }
            }}
          >
            {isFetching && dataId === data.id ? (
              <Spinner size="sm" data-testid="appSwitcher-spinner" />
            ) : (
              (data.desktopLogo || data.mobileLogo) && (
                <MaxHeightImg
                  src={isMobile ? data.mobileLogo : data.desktopLogo}
                  alt={data.alt}
                  width="50px"
                  height="50px"
                />
              )
            )}
          </AppSwitchOption>
        ))}
        <Sidelight />
        <Toast
          data-testid="appSwitcher-error"
          isOpen={!!(hasError && errorMsg)}
          onClose={() => resetAppSwitcherData(dispatch)}
          type="error"
        >
          {errorMsg}
        </Toast>
      </ContainerWithSidelights>
    </ContainerWithBackground>
  );
};

AppSwitcher.propTypes = {
  appSwitcherData: PropTypes.array.isRequired
};
AppSwitcher.displayName = "AppSwitcher";

export default AppSwitcher;
